import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Rocket, Workflow, Users, CalendarDays, CheckSquare, Gift, Mail, UserCircle, Settings,
  LifeBuoy, HelpCircle, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

export const CATEGORY_ICONS = {
  Rocket, Workflow, Users, CalendarDays, CheckSquare, Gift, Mail, UserCircle, Settings,
  LifeBuoy, HelpCircle, BookOpen,
};

// Minimal, safe markdown renderer (headings, bullet lists, bold, paragraphs). No raw HTML.
export const plain = (s) => (s || "").replace(/\*\*/g, "").replace(/^#+\s/gm, "").replace(/`/g, "");
export function MarkdownLite({ text, onOpenArticle }) {
  const lines = (text || "").split("\n");
  const out = [];
  let list = [];
  const flush = (k) => { if (list.length) { out.push(<ul key={`ul-${k}`} className="list-disc pl-5 space-y-1 my-2 text-slate-600">{list}</ul>); list = []; } };
  const inline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(\/help\/[^)\s]+\))/g);
    return parts.map((p, i) => {
      const lk = p.match(/^\[([^\]]+)\]\((\/help\/[^)\s]+)\)$/);
      if (lk) return <Link key={i} to={lk[2]} onClick={onOpenArticle ? (e) => { e.preventDefault(); onOpenArticle(lk[2].split("/").pop()); } : undefined} className="font-medium text-brand underline underline-offset-2 hover:text-brand-strong" data-testid={`article-link-${lk[2].split("/").pop()}`}>{lk[1]}</Link>;
      return p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong>
      : p.length > 2 && p.startsWith("`") && p.endsWith("`")
        ? <code key={i} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.85em] text-slate-800">{p.slice(1, -1)}</code>
        : <span key={i}>{p}</span>;
    });
  };
  lines.forEach((ln, i) => {
    if (ln.startsWith("> ")) { flush(i); out.push(<div key={i} className="my-3 rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-slate-700 leading-relaxed" data-testid="article-callout">{inline(ln.slice(2))}</div>); }
    else if (/^!\[[^\]]*\]\((https:\/\/|\/api\/help\/images\/)[^)\s]+\)$/.test(ln.trim())) { flush(i); const m = ln.trim().match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/); out.push(<img key={i} src={m[2]} alt={m[1]} className="my-3 max-w-full h-auto rounded-lg border border-slate-200" data-testid="article-image" />); }
    else if (ln.startsWith("## ")) { flush(i); out.push(<h3 key={i} className="text-base font-semibold text-slate-900 mt-4 mb-1">{ln.slice(3)}</h3>); }
    else if (ln.startsWith("# ")) { flush(i); out.push(<h2 key={i} className="text-lg font-bold text-slate-900 mt-4 mb-1">{ln.slice(2)}</h2>); }
    else if (/^[-*] /.test(ln)) { list.push(<li key={`li-${i}`}>{inline(ln.slice(2))}</li>); }
    else if (/^\d+\. /.test(ln)) { list.push(<li key={`li-${i}`}>{inline(ln.replace(/^\d+\.\s/, ""))}</li>); }
    else if (ln.trim() === "") { flush(i); }
    else { flush(i); out.push(<p key={i} className="text-sm text-slate-600 leading-relaxed my-1.5">{inline(ln)}</p>); }
  });
  flush("end");
  return <div data-testid="article-body">{out}</div>;
}

// HTML bodies are sanitized on the server when saved (allow-list); rendered with readable defaults.
export const HTML_BODY_CLASS = "text-sm text-slate-600 leading-relaxed space-y-2 break-words [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-slate-900 [&_h1]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-brand [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-slate-200 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-slate-50 [&_th]:text-left [&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3 [&_pre]:bg-slate-50 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto";

export function ArticleBody({ article, onOpenArticle }) {
  if (article?.body_format === "html") {
    return <div className={`${HTML_BODY_CLASS} overflow-x-auto`} data-testid="article-body" dangerouslySetInnerHTML={{ __html: article.body || "" }} />;
  }
  return <MarkdownLite text={article?.body} onOpenArticle={onOpenArticle} />;
}

// Contextual ? help tip — brief text + optional "View full article" link.
export function HelpTip({ text, slug, label, size = 14 }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`help-tip${label ? `-${label}` : ""}`} aria-label="Help"
          className="inline-flex items-center justify-center text-slate-400 hover:text-brand transition-colors align-middle">
          <HelpCircle style={{ width: size, height: size }} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-white w-72 text-sm" data-testid="help-tip-content">
        <p className="text-slate-600 leading-relaxed">{text}</p>
        {slug && <a href={`/help/article/${slug}`} className="text-brand font-medium text-xs mt-2 inline-block hover:underline">View full Help article →</a>}
      </PopoverContent>
    </Popover>
  );
}

// Reuses the existing Help support-request endpoint (safe context only). Not a parallel ticket system.
export function ContactSupportDialog({ open, onClose, articleSlug }) {
  const location = useLocation();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ category: "", subject: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/help/support/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    setForm({ category: "", subject: "", description: "" });
  }, [open]);

  useEffect(() => {
    const q = (form.subject || "").trim();
    if (!open || q.length < 3) { setSuggested([]); return; }
    const t = setTimeout(() => {
      api.get("/help/search", { params: { q } }).then((r) => setSuggested((r.data.items || []).slice(0, 3))).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.subject, open]);

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Please add a subject"); return; }
    setBusy(true);
    try {
      await api.post("/help/support/request", { ...form, page: location.pathname, article_slug: articleSlug });
      toast.success("Support request submitted — we'll be in touch.");
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="contact-support-dialog">
        <DialogHeader><DialogTitle>Contact Support</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="support-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Subject" value={form.subject} data-testid="support-subject"
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          {suggested.length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="support-suggestions">
              <p className="text-xs font-medium text-slate-500 mb-1">These articles might help:</p>
              {suggested.map((s) => <a key={s.slug} href={`/help/article/${s.slug}`} className="block text-sm text-brand hover:underline">{s.title}</a>)}
            </div>
          )}
          <Textarea rows={4} placeholder="Describe your issue" value={form.description} data-testid="support-description"
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <p className="text-[11px] text-slate-400">We include your name, role and current page to help us assist you. We never share passwords or secrets.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="support-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-strong text-white" data-testid="support-submit">Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
